import { getUserInfoFromLocalStorage } from '../../utils/LocalStorageUtils';
import { useNavigate, useLocation } from 'react-router-dom';
import { Avatar, Badge, Tooltip } from '@mui/material';
import { CircleButton } from '../../styles/CustomButtons';
import { useUIState } from '../../context/uiState';
import { UIStateType } from '../../context/uiState/context';
import {
  usePaymentStatus,
  useUserEmail,
  useCreateOrRetrieveCheckoutSessionMutation,
  useCreateCustomerPortalSessionMutation,
  useSubscriptionDetails,
} from '@features/user';
import { unixTimestampToDaysRemaining } from '../../utils/utils';
import { useState, useMemo } from 'react';
import { useEventTracking } from '@services/statsig';

const UserMenuButton = () => {
  const userInfo = getUserInfoFromLocalStorage();
  const navigate = useNavigate();
  const location = useLocation();
  const { state, setState } = useUIState();
  const currentPage = state.navigation.currentPage;
  const paymentStatus = usePaymentStatus();
  const userEmail = useUserEmail();
  const [createOrRetrieveCheckoutSession] = useCreateOrRetrieveCheckoutSessionMutation();
  const [createCustomerPortalSession] = useCreateCustomerPortalSessionMutation();
  const [loadingBilling, setLoadingBilling] = useState(false);
  const { navigation: navigationMetrics } = useEventTracking();

  // Fetch subscription details from the new API
  const { data: subscriptionDetails } = useSubscriptionDetails();

  // Calculate subscription status information
  const subscriptionStatus = useMemo(() => {
    // Check if subscription is paused or canceled
    if (paymentStatus && ['paused', 'canceled'].includes(paymentStatus)) {
      return {
        badgeContent: '!',
        tooltipMessage: 'Your subscription is inactive. Click to restore access to your account.',
        isWarning: true,
      };
    }

    // Check trial status
    if (subscriptionDetails?.status === 'trialing' && subscriptionDetails?.current_period_end) {
      const remainingSeconds = subscriptionDetails.current_period_end - Math.floor(Date.now() / 1000);
      let daysRemaining: number;

      // Always use unixTimestampToDaysRemaining which uses Math.ceil
      // This ensures partial days are rounded up (e.g., 0.5 days -> 1, 9.5 days -> 10)
      daysRemaining = unixTimestampToDaysRemaining(subscriptionDetails.current_period_end);

      if (remainingSeconds <= 86400) {
        // Less than 24 hours - show 1 day remaining with urgent warning
        return {
          badgeContent: `${daysRemaining}`,
          tooltipMessage:
            'Your trial ends in less than 24 hours! Click to continue your subscription.',
          isWarning: true,
        };
      } else {
        const isEnding = daysRemaining <= 3;
        return {
          badgeContent: `${daysRemaining}`,
          tooltipMessage: isEnding
            ? "Your trial is almost up! Click here to access your billing portal so you don't lose access to your notes."
            : `${daysRemaining} days left in trial`,
          isWarning: isEnding,
        };
      }
    }

    return {
      badgeContent: null,
      tooltipMessage: '',
      isWarning: false,
    };
  }, [paymentStatus, subscriptionDetails?.status, subscriptionDetails?.current_period_end]);

  const handleBillingClick = async () => {
    // Track billing quick action
    navigationMetrics.quickAction({
      action_type: 'billing_portal',
      source_path: location.pathname,
    });
    
    setLoadingBilling(true);

    try {
      let data;
      if (paymentStatus && ['canceled'].includes(paymentStatus)) {
        // Generate checkout session
        data = await createOrRetrieveCheckoutSession({}).unwrap();
      } else {
        // Generate billing portal link
        data = await createCustomerPortalSession({}).unwrap();
      }

      const url =
        'portal_session_url' in data
          ? data.portal_session_url
          : 'checkout_session_url' in data
            ? data.checkout_session_url
            : undefined;

      if (url) {
        window.open(url, '_blank');
      } else {
        // Fallback to default Stripe billing portal
        window.open(
          `https://billing.stripe.com/p/login/7sIdRo5NjdyN1QA8ww?prefilled_email=${encodeURIComponent(
            userEmail || '',
          )}`,
          '_blank',
        );
      }
    } catch (error) {
      console.error('Error creating session:', error);
      // Fallback to default Stripe billing portal
      window.open(
        `https://billing.stripe.com/p/login/7sIdRo5NjdyN1QA8ww?prefilled_email=${encodeURIComponent(
          userEmail || '',
        )}`,
        '_blank',
      );
    } finally {
      setLoadingBilling(false);
    }
  };

  return (
    <Tooltip title={subscriptionStatus.tooltipMessage} placement="bottom" arrow>
      <CircleButton
        onClick={e => {
          // Check if the click was on the badge
          const badge = e.currentTarget.querySelector('.MuiBadge-badge');
          if (subscriptionStatus.badgeContent && badge && badge.contains(e.target as Node)) {
            handleBillingClick();
          } else {
            // Track user avatar click navigation
            navigationMetrics.clicked({
              menu_item: 'user_avatar',
              source_path: location.pathname,
              destination_path: '/user',
              is_dropdown: false,
            });
            setState((prevState: UIStateType) => ({
              ...prevState,
              navigation: { currentPage: 'user' },
            }));
            navigate('/user');
          }
        }}
        sx={{ position: 'relative' }}
        disabled={loadingBilling}
      >
        {subscriptionStatus.badgeContent ? (
          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            badgeContent={subscriptionStatus.badgeContent}
            color={subscriptionStatus.isWarning ? 'error' : 'info'}
            sx={{
              '& .MuiBadge-badge': {
                fontSize: '0.6rem',
                minWidth: '18px',
                height: '18px',
                padding: '0 4px',
                fontWeight: 'bold',
                cursor: 'pointer',
              },
            }}
          >
            <Avatar variant="circular" src={userInfo?.img} alt="user photo"></Avatar>
          </Badge>
        ) : (
          <Avatar variant="circular" src={userInfo?.img} alt="user photo"></Avatar>
        )}
      </CircleButton>
    </Tooltip>
  );
};

export default UserMenuButton;
